import httpCommon, { httpFormData } from "./http-common";
import { AxiosProgressEvent } from "axios";

// Purchase Requisitions

// Get all purchase requisitions
const getAllPurchaseRequisitions = (businessId: number, status?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (status) queryParams += `&status=${status}`;
  return httpCommon.get(`/v1/get/purchase/requisitions/${businessId}${queryParams}`);
};

// Get purchase requisition by ID
const getPurchaseRequisition = (requisitionId: string) => {
  return httpCommon.get(`/v1/get/purchase/requisition/${requisitionId}`);
};

// Create purchase requisition
const createPurchaseRequisition = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/purchase/requisition/${businessId}`, data);
};

// Update purchase requisition
const updatePurchaseRequisition = (requisitionId: string, data: any) => {
  return httpCommon.put(`/v1/update/purchase/requisition/${requisitionId}`, data);
};

// Delete purchase requisition
const deletePurchaseRequisition = (requisitionId: string) => {
  return httpCommon.delete(`/v1/delete/purchase/requisition/${requisitionId}`);
};

// Approve purchase requisition
const approvePurchaseRequisition = (requisitionId: string, data: any) => {
  return httpCommon.post(`/v1/approve/purchase/requisition/${requisitionId}`, data);
};

// Reject purchase requisition
const rejectPurchaseRequisition = (requisitionId: string, data: any) => {
  return httpCommon.post(`/v1/reject/purchase/requisition/${requisitionId}`, data);
};

// Get pending approvals for user
const getPendingApprovals = (businessId: number, userId?: string) => {
  const userParam = userId ? `?userId=${userId}` : '';
  return httpCommon.get(`/v1/get/purchase/pending-approvals/${businessId}${userParam}`);
};

// Purchase Orders

// Get all purchase orders
const getAllPurchaseOrders = (businessId: number, status?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (status) queryParams += `&status=${status}`;
  return httpCommon.get(`/v1/get/purchase/orders/${businessId}${queryParams}`);
};

// Get purchase order by ID
const getPurchaseOrder = (orderId: string) => {
  return httpCommon.get(`/v1/get/purchase/order/${orderId}`);
};

// Create purchase order
const createPurchaseOrder = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/purchase/order/${businessId}`, data);
};

// Generate purchase order from requisition
const generatePurchaseOrderFromRequisition = (requisitionId: string, data: any) => {
  return httpCommon.post(`/v1/generate/purchase/order/${requisitionId}`, data);
};

// Update purchase order
const updatePurchaseOrder = (orderId: string, data: any) => {
  return httpCommon.put(`/v1/update/purchase/order/${orderId}`, data);
};

// Delete purchase order
const deletePurchaseOrder = (orderId: string) => {
  return httpCommon.delete(`/v1/delete/purchase/order/${orderId}`);
};

// Send purchase order to supplier
const sendPurchaseOrderToSupplier = (orderId: string, data: any) => {
  return httpCommon.post(`/v1/send/purchase/order/${orderId}`, data);
};

// Supplier Quotations

// Get all supplier quotations
const getAllSupplierQuotations = (businessId: number, supplierId?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (supplierId) queryParams += `&supplierId=${supplierId}`;
  return httpCommon.get(`/v1/get/supplier/quotations/${businessId}${queryParams}`);
};

// Get supplier quotation by ID
const getSupplierQuotation = (quotationId: string) => {
  return httpCommon.get(`/v1/get/supplier/quotation/${quotationId}`);
};

// Request quotation from supplier
const requestQuotationFromSupplier = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/request/quotation/${businessId}`, data);
};

// Create supplier quotation
const createSupplierQuotation = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/supplier/quotation/${businessId}`, data);
};

// Update supplier quotation
const updateSupplierQuotation = (quotationId: string, data: any) => {
  return httpCommon.put(`/v1/update/supplier/quotation/${quotationId}`, data);
};

// Approve supplier quotation
const approveSupplierQuotation = (quotationId: string, data: any) => {
  return httpCommon.post(`/v1/approve/supplier/quotation/${quotationId}`, data);
};

// Compare quotations
const compareQuotations = (quotationIds: string[]) => {
  return httpCommon.post(`/v1/compare/quotations`, { quotationIds });
};

// Goods Received Vouchers (GRV)

// Get all goods received vouchers
const getAllGoodsReceivedVouchers = (businessId: number, status?: string, skip = 0, limit = 0) => {
  let queryParams = `?skip=${skip}&limit=${limit}`;
  if (status) queryParams += `&status=${status}`;
  return httpCommon.get(`/v1/get/goods-received-vouchers/${businessId}${queryParams}`);
};

// Get goods received voucher by ID
const getGoodsReceivedVoucher = (grvId: string) => {
  return httpCommon.get(`/v1/get/goods-received-voucher/${grvId}`);
};

// Create goods received voucher
const createGoodsReceivedVoucher = (businessId: number, data: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  if (data instanceof FormData) {
    return httpFormData.post(`/v1/add/goods-received-voucher/${businessId}`, data, {
      onUploadProgress: onUploadProgress
    });
  }
  return httpCommon.post(`/v1/add/goods-received-voucher/${businessId}`, data);
};

// Update goods received voucher
const updateGoodsReceivedVoucher = (grvId: string, data: any, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  if (data instanceof FormData) {
    return httpFormData.put(`/v1/update/goods-received-voucher/${grvId}`, data, {
      onUploadProgress: onUploadProgress
    });
  }
  return httpCommon.put(`/v1/update/goods-received-voucher/${grvId}`, data);
};

// Approve goods received voucher
const approveGoodsReceivedVoucher = (grvId: string, data: any) => {
  return httpCommon.post(`/v1/approve/goods-received-voucher/${grvId}`, data);
};

// Verify goods received voucher against purchase order
const verifyGoodsReceivedVoucher = (grvId: string) => {
  return httpCommon.post(`/v1/verify/goods-received-voucher/${grvId}`);
};

// Park goods (temporary storage)
const parkGoods = (grvId: string, data: any) => {
  return httpCommon.post(`/v1/park/goods/${grvId}`, data);
};

// Unpark goods
const unparkGoods = (grvId: string, data: any) => {
  return httpCommon.post(`/v1/unpark/goods/${grvId}`, data);
};

// Supplier Catalogs & Mall

// Get supplier catalogs
const getSupplierCatalogs = (businessId: number, supplierId?: string) => {
  const supplierParam = supplierId ? `?supplierId=${supplierId}` : '';
  return httpCommon.get(`/v1/get/supplier-catalogs/${businessId}${supplierParam}`);
};

// Get supplier catalog by supplier
const getSupplierCatalog = (supplierId: string, category?: string) => {
  const categoryParam = category ? `?category=${category}` : '';
  return httpCommon.get(`/v1/get/supplier-catalog/${supplierId}${categoryParam}`);
};

// Search supplier catalog
const searchSupplierCatalog = (supplierId: string, searchTerm: string) => {
  return httpCommon.get(`/v1/search/supplier-catalog/${supplierId}/${searchTerm}`);
};

// Add item to supplier catalog
const addItemToSupplierCatalog = (supplierId: string, data: any) => {
  return httpCommon.post(`/v1/add/supplier-catalog/item/${supplierId}`, data);
};

// Update supplier catalog item
const updateSupplierCatalogItem = (catalogItemId: string, data: any) => {
  return httpCommon.put(`/v1/update/supplier-catalog/item/${catalogItemId}`, data);
};

// Remove item from supplier catalog
const removeItemFromSupplierCatalog = (catalogItemId: string) => {
  return httpCommon.delete(`/v1/delete/supplier-catalog/item/${catalogItemId}`);
};

// Sync supplier catalog
const syncSupplierCatalog = (supplierId: string) => {
  return httpCommon.post(`/v1/sync/supplier-catalog/${supplierId}`);
};

// Budget Management

// Get procurement budget allocations
const getProcurementBudgetAllocations = (businessId: number, departmentId?: string) => {
  const deptParam = departmentId ? `?departmentId=${departmentId}` : '';
  return httpCommon.get(`/v1/get/procurement/budget-allocations/${businessId}${deptParam}`);
};

// Create budget allocation
const createBudgetAllocation = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/add/procurement/budget-allocation/${businessId}`, data);
};

// Update budget allocation
const updateBudgetAllocation = (allocationId: string, data: any) => {
  return httpCommon.put(`/v1/update/procurement/budget-allocation/${allocationId}`, data);
};

// Check budget availability
const checkBudgetAvailability = (businessId: number, departmentId: string, amount: number) => {
  return httpCommon.get(`/v1/check/budget-availability/${businessId}/${departmentId}?amount=${amount}`);
};

// Reserve budget
const reserveBudget = (businessId: number, data: any) => {
  return httpCommon.post(`/v1/reserve/budget/${businessId}`, data);
};

// Release budget reservation
const releaseBudgetReservation = (reservationId: string) => {
  return httpCommon.post(`/v1/release/budget-reservation/${reservationId}`);
};

// Analytics & Reports

// Get procurement dashboard data
const getProcurementDashboard = (businessId: number) => {
  return httpCommon.get(`/v1/get/procurement/dashboard/${businessId}`);
};

// Get supplier performance metrics
const getSupplierPerformanceMetrics = (businessId: number, supplierId?: string, startDate?: string, endDate?: string) => {
  let queryParams = '';
  const params = [];
  if (supplierId) params.push(`supplierId=${supplierId}`);
  if (startDate) params.push(`startDate=${startDate}`);
  if (endDate) params.push(`endDate=${endDate}`);
  if (params.length > 0) queryParams = '?' + params.join('&');
  
  return httpCommon.get(`/v1/get/supplier/performance/${businessId}${queryParams}`);
};

// Get procurement analytics
const getProcurementAnalytics = (businessId: number, startDate: string, endDate: string) => {
  return httpCommon.get(`/v1/get/procurement/analytics/${businessId}?startDate=${startDate}&endDate=${endDate}`);
};

// Generate procurement report
const generateProcurementReport = (businessId: number, reportType: string, startDate: string, endDate: string) => {
  return httpCommon.get(`/v1/generate/procurement/report/${businessId}/${reportType}?startDate=${startDate}&endDate=${endDate}`, {
    responseType: 'blob'
  });
};

const ProcurementService = {
  // Purchase Requisitions
  getAllPurchaseRequisitions,
  getPurchaseRequisition,
  createPurchaseRequisition,
  updatePurchaseRequisition,
  deletePurchaseRequisition,
  approvePurchaseRequisition,
  rejectPurchaseRequisition,
  getPendingApprovals,

  // Purchase Orders
  getAllPurchaseOrders,
  getPurchaseOrder,
  createPurchaseOrder,
  generatePurchaseOrderFromRequisition,
  updatePurchaseOrder,
  deletePurchaseOrder,
  sendPurchaseOrderToSupplier,

  // Supplier Quotations
  getAllSupplierQuotations,
  getSupplierQuotation,
  requestQuotationFromSupplier,
  createSupplierQuotation,
  updateSupplierQuotation,
  approveSupplierQuotation,
  compareQuotations,

  // Goods Received Vouchers
  getAllGoodsReceivedVouchers,
  getGoodsReceivedVoucher,
  createGoodsReceivedVoucher,
  updateGoodsReceivedVoucher,
  approveGoodsReceivedVoucher,
  verifyGoodsReceivedVoucher,
  parkGoods,
  unparkGoods,

  // Supplier Catalogs & Mall
  getSupplierCatalogs,
  getSupplierCatalog,
  searchSupplierCatalog,
  addItemToSupplierCatalog,
  updateSupplierCatalogItem,
  removeItemFromSupplierCatalog,
  syncSupplierCatalog,

  // Budget Management
  getProcurementBudgetAllocations,
  createBudgetAllocation,
  updateBudgetAllocation,
  checkBudgetAvailability,
  reserveBudget,
  releaseBudgetReservation,

  // Analytics & Reports
  getProcurementDashboard,
  getSupplierPerformanceMetrics,
  getProcurementAnalytics,
  generateProcurementReport
};

export default ProcurementService; 